import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { db, auth } from '../firebase/config';
import { Lock, ShieldCheck, ArrowLeft } from 'lucide-react';
import { sha256Hex, lockNow } from './SensitiveGate';

// 관리자 잠금(PIN) 설정 — 아이디를 직원과 공유할 때 민감 화면(거래명세서·세금계산서·정산·경영분석 등) 보호
// PIN은 SHA-256 해시로만 저장. 변경·해제는 현재 PIN 또는 계정 비밀번호 재확인 필요 (분실 대비).

function SecurityLockSettings({ user, onBack }) {
    const targetId = user?.companyId || user?.uid;
    const isStaff = !!user?.companyId;

    const [enabled, setEnabled] = useState(false);
    const [hasPin, setHasPin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [currentPin, setCurrentPin] = useState('');
    const [accountPw, setAccountPw] = useState('');
    const [useAccountPw, setUseAccountPw] = useState(false);
    const [newPin, setNewPin] = useState('');
    const [newPin2, setNewPin2] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'users', targetId));
                const d = snap.exists() ? snap.data() : {};
                setEnabled(!!d.sensitiveLockEnabled);
                setHasPin(!!d.sensitivePinHash);
            } catch { /* ignore */ }
            setLoading(false);
        })();
    }, [targetId]);

    // 현재 PIN 또는 계정 비밀번호로 본인 확인
    const verifyIdentity = async () => {
        if (!hasPin) return true;   // 최초 설정은 확인 불필요
        if (useAccountPw) {
            if (!accountPw) { alert('계정 비밀번호를 입력해주세요.'); return false; }
            try {
                const cred = EmailAuthProvider.credential(auth.currentUser.email, accountPw);
                await reauthenticateWithCredential(auth.currentUser, cred);
                return true;
            } catch {
                alert('계정 비밀번호가 올바르지 않습니다.');
                return false;
            }
        }
        if (!currentPin) { alert('현재 PIN을 입력해주세요.'); return false; }
        const snap = await getDoc(doc(db, 'users', targetId));
        const savedHash = snap.exists() ? snap.data().sensitivePinHash : '';
        if (await sha256Hex(currentPin.trim()) !== savedHash) {
            alert('현재 PIN이 올바르지 않습니다.');
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!/^\d{4,6}$/.test(newPin)) { alert('PIN은 숫자 4~6자리로 입력해주세요.'); return; }
        if (newPin !== newPin2) { alert('새 PIN이 서로 일치하지 않습니다.'); return; }
        setSaving(true);
        try {
            if (!(await verifyIdentity())) { setSaving(false); return; }
            await updateDoc(doc(db, 'users', targetId), {
                sensitiveLockEnabled: true,
                sensitivePinHash: await sha256Hex(newPin),
                sensitivePinUpdatedAt: new Date(),
            });
            lockNow();   // 새 PIN 적용 즉시 재잠금
            setHasPin(true); setEnabled(true);
            setCurrentPin(''); setAccountPw(''); setNewPin(''); setNewPin2('');
            alert('관리자 잠금이 설정되었습니다.\n이제 금액·정산 화면 진입 시 PIN이 필요합니다.');
        } catch (e) {
            console.error('잠금 설정 실패:', e);
            alert('저장에 실패했습니다.');
        }
        setSaving(false);
    };

    const handleDisable = async () => {
        if (!window.confirm('관리자 잠금을 해제하시겠습니까?\n민감 화면이 PIN 없이 열리게 됩니다.')) return;
        setSaving(true);
        try {
            if (!(await verifyIdentity())) { setSaving(false); return; }
            await updateDoc(doc(db, 'users', targetId), { sensitiveLockEnabled: false });
            lockNow();
            setEnabled(false);
            setCurrentPin(''); setAccountPw('');
            alert('관리자 잠금이 해제되었습니다.');
        } catch (e) {
            alert('해제에 실패했습니다.');
        }
        setSaving(false);
    };

    if (isStaff) {
        return (
            <div style={{ maxWidth: 560, margin: '40px auto', textAlign: 'center', color: '#64748B', fontSize: 14 }}>
                관리자 잠금은 대표 계정에서만 설정할 수 있습니다.
            </div>
        );
    }
    if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#94A3B8' }}>로딩 중...</div>;

    const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14, border: '1px solid #E2E8F0', borderRadius: 8, outline: 'none' };
    const labelStyle = { fontSize: 12.5, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 5, marginTop: 14 };

    return (
        <div style={{ maxWidth: 560, margin: '0 auto', padding: '10px 16px 60px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '10px 0 18px' }}>
                <button onClick={onBack} style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: 8, padding: 8, cursor: 'pointer' }}>
                    <ArrowLeft size={17} />
                </button>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>관리자 잠금 (PIN)</h2>
            </div>

            <div style={{ background: enabled ? '#F0FDF4' : '#F8FAFC', border: `1px solid ${enabled ? '#BBF7D0' : '#E2E8F0'}`, borderRadius: 12, padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'center', marginBottom: 18 }}>
                <ShieldCheck size={22} color={enabled ? '#15803D' : '#94A3B8'} />
                <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>
                    <b>{enabled ? '잠금 사용 중' : '잠금 꺼짐'}</b><br />
                    <span style={{ color: '#64748B' }}>
                        아이디를 직원과 함께 사용할 때, 거래명세서·세금계산서·정산·경영분석 등 금액 관련 화면 진입 시 PIN을 요구합니다.
                        해제 후 30분간 유지됩니다.
                    </span>
                </div>
            </div>

            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 14, padding: '20px 22px' }}>
                <div style={{ fontSize: 14, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Lock size={15} /> {hasPin ? 'PIN 변경' : 'PIN 등록'}
                </div>

                {hasPin && (
                    <>
                        {!useAccountPw ? (
                            <>
                                <label style={labelStyle}>현재 PIN</label>
                                <input type="password" inputMode="numeric" maxLength={6} value={currentPin}
                                    onChange={e => setCurrentPin(e.target.value.replace(/[^0-9]/g, ''))} style={inputStyle} />
                            </>
                        ) : (
                            <>
                                <label style={labelStyle}>계정 비밀번호 (PIN 분실 시)</label>
                                <input type="password" value={accountPw} onChange={e => setAccountPw(e.target.value)} style={inputStyle} />
                            </>
                        )}
                        <button onClick={() => setUseAccountPw(v => !v)}
                            style={{ background: 'none', border: 'none', color: '#6366F1', fontSize: 12, cursor: 'pointer', padding: '6px 0 0', fontWeight: 600 }}>
                            {useAccountPw ? '현재 PIN으로 확인하기' : 'PIN을 잊으셨나요? 계정 비밀번호로 확인'}
                        </button>
                    </>
                )}

                <label style={labelStyle}>새 PIN (숫자 4~6자리)</label>
                <input type="password" inputMode="numeric" maxLength={6} value={newPin}
                    onChange={e => setNewPin(e.target.value.replace(/[^0-9]/g, ''))} style={inputStyle} />
                <label style={labelStyle}>새 PIN 확인</label>
                <input type="password" inputMode="numeric" maxLength={6} value={newPin2}
                    onChange={e => setNewPin2(e.target.value.replace(/[^0-9]/g, ''))} style={inputStyle} />

                <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                    <button onClick={handleSave} disabled={saving}
                        style={{ flex: 1, padding: '12px 0', background: '#6366F1', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                        {saving ? '저장 중...' : (hasPin ? 'PIN 변경' : '잠금 켜기')}
                    </button>
                    {enabled && (
                        <button onClick={handleDisable} disabled={saving}
                            style={{ padding: '12px 18px', background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', borderRadius: 10, fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
                            잠금 끄기
                        </button>
                    )}
                </div>
            </div>

            <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 14, lineHeight: 1.7 }}>
                · PIN은 암호화(해시)되어 저장되며 원문은 서버에 남지 않습니다.<br />
                · 화면 접근을 막는 관리 기능으로, 완전한 보안 수단은 아닙니다. 가능하면 직원 개별 계정 사용을 권장합니다.
            </div>
        </div>
    );
}

export default SecurityLockSettings;
